import { computed, Injectable, signal, type Signal } from '@angular/core';
import type { jsoneData } from '../../api/interfaces';
import { compileRule, evaluateRules, type RuleUpdate } from './rule-compiler';
import { evaluateRuleExpression } from './rule-evaluator';

/**
 * The `showIfRule` / `autocompleteRule` rules compiled from the module
 * structure, plus their current values.
 *
 * Deliberately knows nothing about the form: it hands computation results back
 * to its caller instead of writing them, which is what keeps this service and
 * `CompilingFormService` out of a dependency cycle.
 */
@Injectable()
export class FormRulesService {
  /** Visibility rules. Internal: read them through `isVisible`. */
  private readonly allShowIf = signal<jsoneData[]>([]);
  /** Rules that compute the value of their own control. Internal: nothing outside reads them. */
  private readonly allComputations = signal<jsoneData[]>([]);

  /**
   * Current visibility per element id.
   *
   * A map, not a scan: sections, sub-sections and widgets each ask about
   * themselves on every change detection pass, and a linear `find` over every
   * rule in the module made that quadratic in the size of the form.
   */
  private readonly visibility = computed(() => {
    const byElement = new Map<string, unknown>();
    for (const rule of this.allShowIf()) {
      byElement.set(String(rule.relatedElement), rule.value);
    }
    return byElement;
  });

  /**
   * A signal reporting whether the element named by `id` is visible.
   *
   * Takes the id as a signal and returns one, so a caller whose id changes
   * derives once instead of rebuilding a `computed` on every read. An element
   * with no rule attached is always visible.
   */
  isVisible(id: Signal<string | undefined>): Signal<boolean> {
    return computed(() => {
      const elementId = id();
      if (!elementId) return false;

      const value = this.visibility().get(elementId);
      // An id with no rule attached is unconditionally visible.
      return value === undefined ? true : !!value;
    });
  }

  /** Registers a `showIfRule`. `false` until evaluated: an unevaluated rule must not reveal anything. */
  addVisibilityRule(source: string, relatedElement: string): void {
    this.allShowIf.update((rules) => [
      ...rules,
      compileRule(source, relatedElement, false),
    ]);
  }

  /** Registers an `autocompleteRule`. */
  addComputationRule(source: string, relatedElement: string): void {
    this.allComputations.update((rules) => [
      ...rules,
      compileRule(source, relatedElement, ''),
    ]);
  }

  /**
   * Re-evaluates both rule sets against a form-value transition and returns the
   * computation results the caller must write back into the form.
   *
   * `force` runs every rule regardless of whether its inputs moved — needed for
   * the first pass after load, where there is no previous value to diff.
   */
  evaluate(
    previousValue: Partial<{ [key: string]: unknown }>,
    currentValue: Partial<{ [key: string]: unknown }>,
    force = false,
  ): RuleUpdate[] {
    const showIf = evaluateRules(
      this.allShowIf(),
      previousValue,
      currentValue,
      force,
      evaluateRuleExpression,
    );
    if (showIf.updates.length > 0) this.allShowIf.set(showIf.rules);

    const computations = evaluateRules(
      this.allComputations(),
      previousValue,
      currentValue,
      force,
      evaluateRuleExpression,
    );
    if (computations.updates.length > 0) {
      this.allComputations.set(computations.rules);
    }

    return computations.updates;
  }
}
